'use client';

import React, {
    useEffect,
    useRef,
    useCallback,
    useState,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BellOff, ChevronRight, CheckCheck } from 'lucide-react';
import { useNotificationCenter } from '@/providers/NotificationCenterProvider';
import { NotificationCard } from './NotificationCard';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';

const modalVariants = {
    hidden: { opacity: 0, y: -8, scale: 0.96 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -8, scale: 0.96 },
};

export function NotificationModal() {
    const {
        isDrawerOpen,
        closeDrawer,
        notifications,
        unreadCount,
        isLoading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loadMore,
        hasMore,
    } = useNotificationCenter();

    const router = useRouter();
    const { buildPath } = useBasePath();

    const [filterTab, setFilterTab] = useState<'all' | 'unread'>('all');

    const scrollRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const lastScrollTrigger = useRef(0);

    /* ---------------------------- Handlers ---------------------------- */

    const handleScroll = useCallback(() => {
        if (!scrollRef.current || isLoading || !hasMore) return;

        const now = Date.now();
        if (now - lastScrollTrigger.current < 300) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        if ((scrollTop + clientHeight) / scrollHeight > 0.8) {
            lastScrollTrigger.current = now;
            loadMore();
        }
    }, [isLoading, hasMore, loadMore]);

    const handleViewAll = useCallback(() => {
        closeDrawer();
        router.push(buildPath('/account/notifications'));
    }, [closeDrawer, router, buildPath]);

    const filteredNotifications = filterTab === 'unread'
        ? notifications.filter(n => !n.isRead)
        : notifications;


    useEffect(() => {
        if (!isDrawerOpen) return;

        modalRef.current?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeDrawer();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isDrawerOpen, closeDrawer]);

    useEffect(() => {
        if (!isDrawerOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const bellButton = document.getElementById('notification-bell-button');
            if (
                modalRef.current &&
                !modalRef.current.contains(e.target as Node) &&
                !bellButton?.contains(e.target as Node)
            ) {
                closeDrawer();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDrawerOpen, closeDrawer]);

    return (
        <AnimatePresence>
            {isDrawerOpen && (
                <>
                    {/* Backdrop */}
                    {/* <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black z-40"
                    /> */}

                    <motion.div
                        ref={modalRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="notification-title"
                        tabIndex={-1}
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ type: 'spring', stiffness: 350, damping: 28 }}
                        className="fixed inset-x-4 top-20 lg:absolute lg:inset-auto lg:top-full lg:right-0 mt-2 lg:w-screen lg:max-w-md bg-white rounded-2xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] z-[100] flex flex-col overflow-hidden border border-gray-100 origin-top-right rounded-lg border border-slate-200 bg-white shadow-lg"
                        style={{ maxHeight: 'calc(100vh - 180px)' }}
                    >
                        {/* Elegant arrow pointer connecting to the bell - only show on desktop */}
                        <div className="hidden lg:block absolute -top-1 right-5 w-3 h-3 bg-white border-l border-t border-gray-100 transform rotate-45 z-10" />
                        {/* Header */}
                        <div className="px-4 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <h2
                                        id="notification-title"
                                        className="text-lg font-semibold text-gray-900"
                                    >
                                        Notifications
                                    </h2>
                                    <span
                                        aria-live="polite"
                                        className="bg-hsecondary text-white text-xs font-semibold px-2.5 py-0.5 rounded-full"
                                    >
                                        {unreadCount} new
                                    </span>
                                </div>

                                <button
                                    onClick={markAllAsRead}
                                    title="Mark all as read"
                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium transition"
                                >
                                    <CheckCheck className="w-4 h-4 inline-block mr-1" />
                                    Mark all read
                                </button>

                            </div>

                            {/* Tabs */}
                            <div className="grid gap-2 grid-cols-2 bg-hsecondary/5 p-1 rounded-2xl">
                                <button
                                    onClick={() => setFilterTab('all')}
                                    className={`py-2 rounded-2xl text-sm font-medium transition ${
                                        filterTab === 'all'
                                            ? 'bg-hsecondary text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setFilterTab('unread')}
                                    className={`px-4 py-2 rounded-2xl text-sm font-medium transition ${
                                        filterTab === 'unread'
                                            ? 'bg-hsecondary text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Unread ({filteredNotifications.filter(n => !n.isRead).length})
                                </button>
                            </div>
                        </div>

                        {/* List */}
                        <div className="flex-1 flex flex-col overflow-hidden">
                            <div
                                ref={scrollRef}
                                onScroll={handleScroll}
                                className="flex-1 overflow-y-auto p-4 space-y-3 bg-white scrollbar-thin scrollbar-thumb-gray-300"
                            >
                                {filteredNotifications.length === 0 && !isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <BellOff className="w-10 h-10 text-gray-300 mb-3" />
                                        <p className="text-sm font-medium text-gray-600">
                                            No notifications yet
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {filteredNotifications.map((notification) => (
                                            <NotificationCard
                                                key={notification._id}
                                                notification={notification}
                                                onMarkAsRead={markAsRead}
                                                onDelete={deleteNotification}
                                                onClose={closeDrawer}
                                            />
                                        ))}

                                        {isLoading && (
                                            <div className="space-y-3 py-2">
                                                {[...Array(3)].map((_, i) => (
                                                    <div
                                                        key={i}
                                                        className="h-16 rounded-lg bg-gray-200 animate-pulse"
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Footer with View All Link */}
                            <div className="px-4 py-3 border-t border-gray-200 bg-white">
                                <button
                                    onClick={handleViewAll}
                                    className="w-full text-center text-sm text-gray-600 hover:text-gray-900 font-medium transition flex items-center justify-center gap-1"
                                >
                                    Go to notification center
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
